import React, { useEffect } from 'react';
import { Coins, PlusCircle, History, Sparkles, ShieldCheck, UserCheck, Calendar, Award, Clock, Crown, Gem } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { Card } from './ui/Card';
import { ProfileAvatar } from './ProfileAvatar';
import { VipBadge, formatVipExpireDate } from './ui/VipBadge';

interface ProfileOverviewProps {
  photoURL?: string | null;
  onAvatarChange: (newUrl: string) => void;
  onOpenAvatarModal?: () => void;
  onOpenTopUp: (initialTab?: 'XU' | 'VIP') => void;
  onOpenHistory?: () => void;
}

export const ProfileOverview: React.FC<ProfileOverviewProps> = ({
  photoURL,
  onAvatarChange,
  onOpenAvatarModal,
  onOpenTopUp,
  onOpenHistory,
}) => {
  const { userProfile, loading } = useAuth();

  // 1. Nguồn dữ liệu & đọc trường Xu chuẩn
  const userCredits = typeof userProfile?.credits === 'number' ? userProfile.credits : 0;
  const vipTierNum = typeof userProfile?.vipTier === 'number' ? userProfile.vipTier : (userProfile?.vipTier ? Number(userProfile.vipTier) : 0);

  // Format ngày tạo tài khoản
  const formatJoinedDate = (dateStr?: string) => {
    if (!dateStr) return 'Mới tham gia';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'Mới tham gia';
      return d.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Mới tham gia';
    }
  };

  return (
    <div className="space-y-6">
      {/* Thẻ Thông tin Đại diện & Role */}
      <Card title="Hồ sơ cá nhân">
        <div className="flex flex-col space-y-5">
          <ProfileAvatar
            currentUrl={photoURL || userProfile?.photoURL || null}
            vipTier={userProfile?.vipTier}
            vipExpireAt={userProfile?.vipExpireAt}
            onImageChange={onAvatarChange}
            onOpenAvatarModal={onOpenAvatarModal}
          />

          <div className="space-y-3 pt-2">
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Tên hiển thị</div>
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <span className="text-sm font-bold text-slate-800">
                  {userProfile?.displayName || 'Chưa cập nhật tên'}
                </span>
                {/* Badge VIP / SVIP kế bên Tên người dùng */}
                <VipBadge
                  vipTier={userProfile?.vipTier}
                  vipExpireAt={userProfile?.vipExpireAt}
                  size="sm"
                />
              </div>

              {/* Nút nâng cấp hoặc hiển thị hạn VIP kèm nút Nâng cấp/Gia hạn SVIP */}
              {vipTierNum === 0 ? (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => onOpenTopUp('VIP')}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-amber-950 font-black text-xs rounded-xl shadow-2xs hover:shadow-xs transition-all active:scale-95 cursor-pointer border border-amber-300/80"
                  >
                    <Crown className="w-3.5 h-3.5 text-amber-950" />
                    <span>Nâng cấp VIP</span>
                  </button>
                </div>
              ) : vipTierNum === 1 ? (
                <div className="mt-2 flex items-center space-x-2 flex-wrap gap-y-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-bold bg-amber-50 text-amber-800 border-amber-200">
                    <Clock className="w-3 h-3 mr-1 text-amber-600" />
                    <span>Hạn VIP: {formatVipExpireDate(userProfile?.vipExpireAt)}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenTopUp('VIP')}
                    className="inline-flex items-center space-x-1 px-2.5 py-1 bg-gradient-to-r from-rose-600 via-red-500 to-amber-500 hover:from-rose-700 hover:to-amber-600 text-white font-black text-[11px] rounded-lg shadow-2xs hover:shadow-xs transition-all active:scale-95 cursor-pointer border border-rose-300/80"
                  >
                    <Gem className="w-3 h-3" />
                    <span>Nâng cấp SVIP</span>
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex items-center space-x-2 flex-wrap gap-y-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-bold bg-rose-50 text-rose-700 border-rose-200">
                    <Clock className="w-3 h-3 mr-1 text-rose-500" />
                    <span>Hạn SVIP: {formatVipExpireDate(userProfile?.vipExpireAt)}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenTopUp('VIP')}
                    className="inline-flex items-center space-x-1 px-2.5 py-1 bg-gradient-to-r from-amber-500 to-rose-600 hover:from-amber-600 hover:to-rose-700 text-white font-black text-[11px] rounded-lg shadow-2xs hover:shadow-xs transition-all active:scale-95 cursor-pointer border border-amber-300/80"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Gia hạn SVIP</span>
                  </button>
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Email đăng ký</div>
              <div className="text-sm font-medium text-slate-600 truncate">
                {userProfile?.email || 'N/A'}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Nhóm thành viên</div>
                {userProfile?.role === 'admin' ? (
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-black rounded-lg border bg-[#224783]/10 text-[#224783] border-[#224783]/30">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1 text-[#224783]" />
                    admin
                  </span>
                ) : vipTierNum === 2 ? (
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-black rounded-lg border bg-rose-50 text-rose-700 border-rose-300">
                    <Gem className="w-3.5 h-3.5 mr-1 text-rose-600" />
                    SVIP
                  </span>
                ) : vipTierNum === 1 ? (
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-black rounded-lg border bg-amber-50 text-amber-800 border-amber-300">
                    <Crown className="w-3.5 h-3.5 mr-1 text-amber-600" />
                    VIP
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-black rounded-lg border bg-slate-100 text-slate-700 border-slate-200">
                    <UserCheck className="w-3.5 h-3.5 mr-1 text-slate-600" />
                    user
                  </span>
                )}
              </div>

              <div className="text-right">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Ngày tham gia</div>
                <div className="text-xs font-semibold text-slate-600 flex items-center justify-end space-x-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <span>{formatJoinedDate(userProfile?.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* 2.B THẺ TỔNG QUAN XU KHẢ DỤNG (XU STAT CARD) */}
      <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-500/5 border-amber-200/80 shadow-xs relative overflow-hidden">
        <div className="absolute -right-6 -bottom-6 w-28 h-28 bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />

        <div className="space-y-4 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 rounded-xl bg-amber-400 text-amber-950 flex items-center justify-center font-bold shadow-xs">
                <Coins className="w-5 h-5 text-amber-950" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                  Số dư Xu
                </h3>
                <p className="text-[11px] text-amber-700 font-medium">Số Xu khả dụng của bạn</p>
              </div>
            </div>
          </div>

          {/* C. Trạng thái Đang tải (Pulse Skeleton) */}
          <div className="py-1">
            {loading ? (
              <div className="space-y-2">
                <div className="h-8 bg-amber-200/50 animate-pulse rounded-lg w-40" />
                <div className="h-3 bg-amber-100 animate-pulse rounded w-28" />
              </div>
            ) : (
              <div className="flex items-baseline space-x-2">
                <span className="text-3xl font-black text-amber-950 tracking-tight">
                  {userCredits.toLocaleString('vi-VN')}
                </span>
                <span className="text-base font-extrabold text-amber-800">Xu</span>
              </div>
            )}
          </div>

          {/* Hành động: Nạp thêm Xu & Lịch sử giao dịch */}
          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-amber-200/60">
            <button
              type="button"
              onClick={onOpenTopUp}
              className="w-full inline-flex items-center justify-center space-x-1.5 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold rounded-xl shadow-xs transition-all active:scale-98"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              <span>Nạp thêm Xu</span>
            </button>

            <button
              type="button"
              onClick={onOpenHistory}
              className="w-full inline-flex items-center justify-center space-x-1.5 px-3 py-2 bg-white hover:bg-amber-100/50 text-amber-900 border border-amber-300/80 text-xs font-semibold rounded-xl transition-all active:scale-98 shadow-2xs"
            >
              <History className="w-3.5 h-3.5 text-amber-700" />
              <span>Lịch sử</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Thẻ Chỉ số Cộng đồng (nếu có) */}
      {userProfile?.communityStats && (
        <Card title="Chỉ số Cộng đồng" className="bg-gradient-to-br from-indigo-50/40 to-blue-50/20">
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <span className="text-xs font-medium text-slate-500">Danh hiệu</span>
              <span className="text-xs font-bold px-2.5 py-0.5 bg-indigo-100 text-indigo-800 rounded-full flex items-center">
                <Award className="w-3 h-3 mr-1 text-indigo-600" />
                {userProfile.communityStats.rankTitle || 'Thành viên Tích cực'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center text-xs">
              <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <div className="font-extrabold text-slate-800 text-sm">
                  {(userProfile.communityStats.reputationPoints || 0).toLocaleString('vi-VN')}
                </div>
                <div className="text-slate-500 text-[11px] font-medium mt-0.5">Điểm uy tín</div>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <div className="font-extrabold text-slate-800 text-sm">
                  {(userProfile.communityStats.postCount || 0).toLocaleString('vi-VN')}
                </div>
                <div className="text-slate-500 text-[11px] font-medium mt-0.5">Bài viết</div>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <div className="font-extrabold text-slate-800 text-sm">
                  {(userProfile.communityStats.followerCount || 0).toLocaleString('vi-VN')}
                </div>
                <div className="text-slate-500 text-[11px] font-medium mt-0.5">Người theo dõi</div>
              </div>
              <div className="bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <div className="font-extrabold text-slate-800 text-sm">
                  {(userProfile.communityStats.weeklyPoints || 0)} / {(userProfile.communityStats.monthlyPoints || 0)}
                </div>
                <div className="text-slate-500 text-[11px] font-medium mt-0.5">Điểm Tuần / Tháng</div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProfileOverview;
